import { FC, useCallback, useEffect, useReducer, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Tab, Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"

import { ModalAddUserBonus } from "features/user/actions/AddUserBonus/ModalAddUserBonus"
import { ModalSendMessage } from "features/user/actions/SendMessage/ModalSendMessage"
import { UserAffiliateTab } from "features/user/tab-contents/affiliate/UserAffiliateTab"
import { RiskTab } from "features/user/tab-contents/RiskTab"
import { UserKycDocs } from "features/user/tab-contents/UserKycDocs"
import { UserOverview } from "features/user/tab-contents/UserOverview"
import { UserPlayerStatistics } from "features/user/tab-contents/UserPlayerStatistics"

import { enumUserTypes } from "constants/user"

import { GetSingleUser } from "services/user/type"
import { userService } from "services/user/user.service"

import { Maybe } from "types/common"

import { ModalEditBalance } from "../components/modals/ModalEditBalance"
import { UserTagsTab } from "../features/user/tab-contents/UserTagsTab"

export const User: FC = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const [updateKey, forceUpdate] = useReducer((x) => x + 1, 0)
  const [editBalance, setEditBalance] = useState<any>(null)
  const [userResult, setUserResult] = useState<Maybe<GetSingleUser>>(null)
  const [loading, setLoading] = useState(false)
  const [currentTab, setCurrentTab] = useState<enumUserTypes>(enumUserTypes.Overview)
  const [openAddBonus, setOpenAddBonus] = useState(false)
  const [openSendMessage, setOpenSendMessage] = useState(false)

  const onLoad = useCallback(async () => {
    try {
      if (!id) return
      setLoading(true)
      const result = await userService.getUserById(id)
      if (result) {
        setUserResult(result)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    onLoad()
  }, [editBalance])

  const onDisable2FA = useCallback(async (): Promise<void> => {
    if (!userResult) return
    try {
      await userService.disableUserTwoFA(userResult.id)
      toast.success(String(t("auth.twoFa.successDisable")) || "2FA disabled successfully")
      onLoad()
    } catch (error: any) {
      toast.error(String(error?.data?.message ?? error?.message ?? t("auth.twoFa.errorDisable")))
    }
  }, [userResult, onLoad, t])

  const editBalanceHandler = useCallback(() => {
    setEditBalance(null)
  }, [])

  const handleAddBonusSuccess = useCallback(() => {
    onLoad()
    forceUpdate()
  }, [])

  const handleToggleExclusion = useCallback(async () => {
    if (!userResult) return

    const now = new Date()

    const isTemporarilyExcluded =
      !!userResult.exclusion_start &&
      !!userResult.exclusion_end &&
      now >= new Date(userResult.exclusion_start) &&
      now <= new Date(userResult.exclusion_end)

    const isPermanentlyExcluded = userResult.activated === false && userResult.exclusion_end === null

    const isExcluded = isTemporarilyExcluded || isPermanentlyExcluded

    try {
      const payload = {
        exclusion_period: isExcluded ? "0" : "permanent"
      }

      await userService.editUser(userResult.id, payload)
      toast.success(isExcluded ? "Exclusion removed" : "User excluded permanently")
      onLoad()
    } catch (error: any) {
      toast.error(error?.data?.message ?? error?.message ?? "Failed to update exclusion")
    }
  }, [userResult, onLoad])

  const userTabs: Tab[] = [
    { key: enumUserTypes.Overview, label: t("user.tabs.overview") },
    { key: enumUserTypes.PlayerStatistics, label: t("user.tabs.playerStatistics") },
    { key: enumUserTypes.KycDocuments, label: t("user.tabs.kycDocuments") },
    { key: enumUserTypes.Tags, label: t("user.tabs.tags") },
    { key: enumUserTypes.Affiliate, label: t("user.tabs.affiliate") },
    { key: enumUserTypes.RiskFraud, label: t("user.tabs.riskFraud") }
  ]

  const renderTab = () => {
    switch (currentTab) {
      case enumUserTypes.Overview:
        return (
          <UserOverview
            userResult={userResult}
            setEditBalance={setEditBalance}
            setOpenAddBonus={setOpenAddBonus}
            setOpenSendMessage={setOpenSendMessage}
            handleToggleExclusion={handleToggleExclusion}
            updateKey={updateKey}
            handleAddBonusSuccess={handleAddBonusSuccess}
            forceUpdate={forceUpdate}
            onLoad={onLoad}
            setUserResult={setUserResult}
            handleDisable2FA={onDisable2FA}
          />
        )
      case enumUserTypes.PlayerStatistics:
        return (
          <UserPlayerStatistics
            userResult={userResult}
            setEditBalance={setEditBalance}
            handleToggleExclusion={handleToggleExclusion}
            updateKey={updateKey}
            handleAddBonusSuccess={handleAddBonusSuccess}
            setOpenAddBonus={setOpenAddBonus}
            setOpenSendMessage={setOpenSendMessage}
          />
        )
      case enumUserTypes.Tags:
        return <UserTagsTab userId={userResult?.id} refreshKey={updateKey} />
      case enumUserTypes.Affiliate:
        return <UserAffiliateTab userId={userResult?.id} affiliates={userResult?.affiliates ?? []} onUpdated={onLoad} />
      case enumUserTypes.RiskFraud:
        return <RiskTab userId={userResult?.id} />
      case enumUserTypes.KycDocuments:
        return (
          <UserKycDocs
            userResult={userResult}
            handleToggleExclusion={handleToggleExclusion}
            updateKey={updateKey}
            handleAddBonusSuccess={handleAddBonusSuccess}
            setOpenAddBonus={setOpenAddBonus}
            setOpenSendMessage={setOpenSendMessage}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
      <TopBar title={t("pages.user.title")} />
      <div className="py-3">
        <Tabs tabs={userTabs} activeTab={currentTab} onChange={(key) => setCurrentTab(key as enumUserTypes)} variant="pills" />
      </div>
      {loading ? (
        <div className="grid place-items-center h-screen">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        renderTab()
      )}

      {userResult && <ModalEditBalance data={userResult} handler={editBalanceHandler} open={editBalance} />}
      {userResult && (
        <ModalAddUserBonus
          open={openAddBonus}
          onClose={() => setOpenAddBonus(false)}
          userId={userResult?.id}
          coin={userResult?.bonus_native_coin}
          onSuccess={handleAddBonusSuccess}
        />
      )}
      {userResult && (
        <ModalSendMessage
          open={openSendMessage}
          onClose={() => setOpenSendMessage(false)}
          userId={userResult.id}
          recipientEmail={userResult.email_address}
        />
      )}
    </div>
  )
}
